import { useState } from 'react';

import { m } from 'framer-motion';
import Stack from '@mui/material/Stack';
import Drawer from '@mui/material/Drawer';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { useBoolean } from 'src/hooks/use-boolean';
import { useResponsive } from 'src/hooks/use-responsive';
import Iconify from 'src/components/iconify';
import AssumptionsEditor from 'src/sections/scenarios/assumptions-editor';
import { varHover } from 'src/components/animate';
import { useFinanceContext } from 'src/components/plaid/datasource';
import AddScenarioDialog from 'src/sections/scenarios/add-scenario-dialog';
import { DEFAULT_SCENARIO } from 'src/components/plaid/datasource/scenario-environments';

// ----------------------------------------------------------------------

const SCENARIO_LABEL = 'Assumptions & Scenarios';

export const useScenarios = () => {
  const { scenarioService, scenarioAssumptions } = useFinanceContext();

  const currentValue =
    Object.keys(scenarioAssumptions ?? {}).length === 0 ? DEFAULT_SCENARIO : scenarioAssumptions;

  const isActive = currentValue?.id !== 'default';
  const makeOption = ({ id, name, description }) => ({ id, name, description });

  const options = (
    scenarioService?.scenarios?.map((e) => ({
      name: e.name,
      id: e.id,
      description: e.description,
    })) ?? []
  )
    .concat(
      { name: 'Add Scenario', id: 'add' },
      !scenarioService?.scenarios?.find((e) => e.id === 'default') ? DEFAULT_SCENARIO : null
    )
    .filter((e) => e)
    .map(makeOption);

  const [showAddScenario, setShowAddScenario] = useState(false);
  const drawer = useBoolean();
  const { scenario: activeScenario, setScenario } = useFinanceContext();

  return {
    activeScenario,
    setScenario,
    drawer,
    showAddScenario,
    setShowAddScenario,
    options,
    isActive,
    currentValue,
    makeOption,
  };
};

export default function ScenariosPopover() {
  const { scenario, setScenario } = useFinanceContext();
  const viewModel = useScenarios();
  const { drawer, options, isActive, currentValue, makeOption, setShowAddScenario } = viewModel;

  return (
    <>
      <Stack>
        <Autocomplete
          size="small"
          id="category-select"
          options={options}
          sx={{ minWidth: '175px', maxWidth: '200px' }}
          value={makeOption(currentValue)}
          disableClearable
          isOptionEqualToValue={(o, v) => o?.id === v?.id}
          getOptionKey={(option) => option?.id ?? 'default'}
          getOptionLabel={(option) => option?.name ?? 'Base Case'}
          onChange={(e, { id }) => {
            if (id === 'add') {
              setShowAddScenario(true);
              drawer.onTrue();
            } else {
              if (id === scenario) drawer.onTrue();
              else {
                setScenario(id);
              }
            }
          }}
          renderOption={(props, option) => (
            <li {...props} key={option?.id ?? 'default'}>
              <div>
                <div>{option?.name ?? 'Base Case'}</div>
                {option?.description && (
                  <div style={{ fontSize: '0.8em', color: 'gray' }}>
                    {option?.description ?? 'No description available'}
                  </div>
                )}
              </div>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label={SCENARIO_LABEL}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start" sx={{ width: 20 }}>
                    <IconButton
                      aria-label="edit scenario"
                      onClick={drawer.onTrue}
                      color={isActive ? 'primary' : 'default'}
                      component={m.button}
                      whileTap="tap"
                      whileHover="hover"
                      variants={varHover(1.05)}
                    >
                      <Iconify icon="mdi:puzzle" width={24} />
                    </IconButton>
                    {/* {currentValue?.description && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ position: 'absolute', left: '55px', bottom: '0px' }}
                      >
                        {currentValue?.description}
                      </Typography>
                    )} */}
                  </InputAdornment>
                ),
              }}
              // size="small"
            />
          )}
        />
      </Stack>
      <ScenarioDrawer viewModel={viewModel} />
    </>
  );
}

export function ScenarioDrawer({ viewModel }) {
  const { showAddScenario, setShowAddScenario, setScenario, drawer } = viewModel;

  const smUp = useResponsive('up', 'sm');

  const handleCloseAddScenario = (newScenario) => {
    if (newScenario) setScenario(newScenario?.id);
    setShowAddScenario(false);
  };

  const renderHead = (
    <Stack
      direction="row"
      alignItems="center"
      sx={{ py: 2, pl: 2.5, pr: 1, minHeight: 68 }}
      spacing={1}
    >
      <Iconify icon="mdi:puzzle" />
      <Typography variant="h6" sx={{ flexGrow: 1 }}>
        {SCENARIO_LABEL}
      </Typography>

      {!smUp && (
        <IconButton onClick={drawer.onFalse}>
          <Iconify icon="mingcute:close-line" />
        </IconButton>
      )}
    </Stack>
  );

  return (
    <Drawer
      open={drawer.value}
      onClose={drawer.onFalse}
      anchor="right"
      slotProps={{
        backdrop: { invisible: true },
      }}
      PaperProps={{
        sx: { width: 1, maxWidth: 420 },
      }}
    >
      {renderHead}

      <Divider />

      <AssumptionsEditor closeDrawer={drawer.onFalse} />
      <AddScenarioDialog open={showAddScenario} onClose={handleCloseAddScenario} />
    </Drawer>
  );
}
